import mixpanel from 'mixpanel-browser';
import { datadogRum } from '@datadog/browser-rum';
import { pick } from 'lodash';
import { store } from 'store';
import { selectCompany, selectUac } from 'store/common/selectors/profile';
import { selectSessionToken } from 'features/onboarding/state/selectors';
import { sanitize } from './sanitize';
import { getDeviceModel, getDynamicProfile, getStaticDeviceProfile } from './deviceProfile';
import {
    EXIT_TRACK_OPTIONS,
    INSTANCE_NAME,
    MIXPANEL_CONFIG,
    MIXPANEL_TOKEN,
    PROFILE_KEYS,
    SDK_VERSIONS,
    TRACKING_VERSION,
} from './constants';

let instance = null;

const safely =
    (fn) =>
    (...args) => {
        try {
            return fn(...args);
        } catch (error) {
            datadogRum.addError(error);
            return false;
        }
    };

const superProps = (state) => {
    const company = selectCompany(state);

    return sanitize({
        tracking_version: TRACKING_VERSION,
        app_version: process.env.REACT_APP_VERSION,
        env: process.env.REACT_APP_ENV,
        company_uuid: company?.uuid,
        company_name: company?.name,
        link_uuid: selectSessionToken(state),
        regula_sdk_version: SDK_VERSIONS.REGULA,
        aws_liveness_version: SDK_VERSIONS.AWS_LIVENESS,
        ...getStaticDeviceProfile(),
    });
};

const registerDeviceModel = () =>
    getDeviceModel().then(
        safely((model) => {
            if (model) instance.register({ device_model: model });
        })
    );

const identify = (uac) => {
    const identifiedUac = instance.get_property('$user_id');

    if (identifiedUac === uac) return;

    // One browser can serve several users (QA runs many links); the last user's id and super
    // properties must not carry over to the next.
    if (identifiedUac) {
        instance.reset();
        void registerDeviceModel();
    }

    // identify() sends `$identify` whenever the id changes, so it only runs once v2 is in use.
    instance.identify(uac);
};

// Identity and super properties are read from the store on every call, so they always match the
// current user, company and link.
const getInstance = () => {
    const state = store.getState();
    const uac = selectUac(state);

    if (!MIXPANEL_TOKEN || !uac) return null;

    if (!instance) {
        instance = mixpanel.init(MIXPANEL_TOKEN, MIXPANEL_CONFIG, INSTANCE_NAME);
        void registerDeviceModel();
    }

    identify(uac);
    instance.register(superProps(state));

    return instance;
};

const send = (event, properties, options) => {
    const target = getInstance();

    if (!target) return false;

    target.track(
        event,
        sanitize({
            ...getDynamicProfile(),
            dd_session_id: datadogRum.getInternalContext()?.session_id,
            ...properties,
        }),
        options
    );

    return true;
};

export const track = safely((event, properties) => send(event, properties));

export const trackOnExit = safely((event, properties) =>
    send(event, properties, EXIT_TRACK_OPTIONS)
);

export const register = safely((properties) => {
    const target = getInstance();

    if (!target) return false;

    target.register(sanitize(properties));
    return true;
});

export const setProfile = safely((properties) => {
    const target = getInstance();

    if (!target) return false;

    target.people.set(sanitize(pick(properties, PROFILE_KEYS)));
    return true;
});
